import { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Inbox, RefreshCw, Zap, Link2, Receipt, Banknote, HandCoins, Clock,
  CheckCircle2, AlertTriangle, MoreHorizontal, ArrowDownCircle, ArrowUpCircle, ExternalLink, FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { cur } from '@/lib/currency';
import { statementStatus } from '@/lib/reconStatus';
import { PageInfoCallout } from '@/components/common/PageInfoCallout';

const REC_META = {  reconciled: { label: 'Reconciled', tone: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  auto: { label: 'Auto-match', tone: 'bg-sky-100 text-sky-700 border-sky-200', icon: Zap },
  confirm: { label: 'Confirm', tone: 'bg-amber-100 text-amber-700 border-amber-200', icon: AlertTriangle },
  review: { label: 'Review', tone: 'bg-stone-100 text-stone-600 border-stone-200', icon: Clock },
};

const PAYEE_TYPES = [
  { key: 'supplier', label: 'Supplier', endpoint: '/suppliers', nameField: 'name' },
  { key: 'customer', label: 'Customer', endpoint: '/customers', nameField: 'name' },
  { key: 'employee', label: 'Employee', endpoint: '/employees', nameField: 'full_name' },
  { key: 'partner', label: 'Partner', endpoint: '/partners', nameField: 'name' },
];

// One-glance status for the dropdown — shared with the Bank Statements list
// via lib/reconStatus so both pages use the SAME definition.

export default function ReconciliationInboxPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const stmtParam = searchParams.get('stmt') || '';
  const filterParam = searchParams.get('filter') || '';

  const [statements, setStatements] = useState([]);
  const [selectedId, setSelectedId] = useState(stmtParam);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoRunning, setAutoRunning] = useState(false);
  const [branches, setBranches] = useState([]);
  const [autoOnUpload, setAutoOnUpload] = useState(true);
  // iter928 — per-user saved preference for the "Needs action" filter, reusing
  // the same localStorage pattern as Bank Statements, keyed by user id. The
  // deep-link ?filter=review still takes precedence (unchanged behavior);
  // otherwise fall back to the saved choice; default OFF when unset.
  const _needsActionPrefKey = `recon_needs_action_only:${(JSON.parse(localStorage.getItem('user') || '{}').id) || 'anon'}`;
  const [needsActionOnly, setNeedsActionOnly] = useState(
    () => (filterParam === 'review' ? true : localStorage.getItem(_needsActionPrefKey) === 'true'),
  );

  // dialog state
  const [dialog, setDialog] = useState(null); // {type, row}

  const loadStatements = useCallback(async () => {
    try {
      const { data } = await api.get('/recon/inbox?limit=100');
      setStatements(data.statements || []);
      if (!selectedId && !stmtParam && (data.statements || []).length) {
        setSelectedId(data.statements[0].id);
      }
    } catch (e) {
      toast.error('Failed to load statements');
    }
  }, [selectedId, stmtParam]);

  const loadDetail = useCallback(async (sid) => {
    if (!sid) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/recon/inbox/${sid}`);
      setDetail(data);
    } catch (e) {
      toast.error('Failed to classify statement');
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatements(); }, []); // eslint-disable-line
  useEffect(() => { if (selectedId) loadDetail(selectedId); }, [selectedId, loadDetail]);
  useEffect(() => {
    api.get('/branches').then(({ data }) => setBranches(data || [])).catch(() => {});
    api.get('/recon/auto-settings').then(({ data }) => setAutoOnUpload(!!data.auto_reconcile_on_upload)).catch(() => {});
  }, []);

  // Real-time freshness: re-fetch from the existing endpoints whenever the tab
  // regains focus / becomes visible, so another user's/process's changes show
  // up immediately without constant polling. Reuses loadDetail + loadStatements.
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === 'visible') {
        loadStatements();
        if (selectedId) loadDetail(selectedId);
      }
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [selectedId, loadDetail, loadStatements]);

  // iter928 — persist the "Needs action" filter choice per user (reuses the
  // existing localStorage preference pattern; frontend-only, no backend).
  useEffect(() => {
    localStorage.setItem(_needsActionPrefKey, String(needsActionOnly));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsActionOnly]);

  // Keep the URL in sync so the deep-link (?stmt=&filter=) is shareable/back-safe.
  const selectStatement = (sid) => {
    setSelectedId(sid);
    const next = {};
    if (sid) next.stmt = sid;
    if (needsActionOnly) next.filter = 'review';
    setSearchParams(next, { replace: true });
  };

  const toggleAutoOnUpload = async (val) => {
    setAutoOnUpload(val);
    try {
      await api.put('/recon/auto-settings', { enabled: val });
      toast.success(val ? 'Auto-reconcile on import turned ON' : 'Auto-reconcile on import turned OFF');
    } catch (e) {
      setAutoOnUpload(!val);
      toast.error('Could not update setting');
    }
  };

  const runAuto = async () => {
    if (!selectedId) return;
    setAutoRunning(true);
    try {
      const { data } = await api.post(`/recon/auto-reconcile/${selectedId}`);
      const c = data.counts || {};
      toast.success(
        `Auto-reconcile done — ${c.auto_reconciled || 0} linked, ${c.already_reconciled || 0} already reconciled, ${c.needs_confirm || 0} to confirm, ${c.review || 0} to review`,
      );
      await loadDetail(selectedId);
      await loadStatements();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Auto-reconcile failed');
    } finally {
      setAutoRunning(false);
    }
  };

  const applyAction = async (payload) => {
    try {
      const { data } = await api.post('/recon/apply', { stmt_id: selectedId, ...payload });
      if (data.already_reconciled) {
        toast.info('Already reconciled — not posted again');
      } else {
        toast.success('Done');
      }
      setDialog(null);
      await loadDetail(selectedId);
      await loadStatements();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
  };

  const unlink = async (row) => {
    try {
      await api.post('/recon/unlink', { stmt_id: selectedId, txn_index: row.txn_index });
      toast.success('Reconciliation reversed (accounting record kept)');
      await loadDetail(selectedId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Unlink failed');
    }
  };

  const summary = detail?.summary || {};

  const setNeedsActionFilter = (val) => {
    setNeedsActionOnly(val);
    const next = {};
    if (selectedId) next.stmt = selectedId;
    if (val) next.filter = 'review';
    setSearchParams(next, { replace: true });
  };

  // Reuse the classifier's per-row `recommendation` already returned by the
  // existing /recon/inbox/{id} endpoint. "Needs action" = MEDIUM (confirm) +
  // LOW/NONE (review). Already auto-reconciled rows (recommendation
  // 'reconciled') are excluded automatically.
  const visibleRows = (detail?.rows || []).filter((r) =>
    needsActionOnly ? (r.recommendation === 'confirm' || r.recommendation === 'review') : true,
  );

  return (
    <DashboardLayout>
      <div className="space-y-6 p-1" data-testid="reconciliation-inbox-page">
        {/* Header */}
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-indigo-600 p-2.5 text-white shadow-sm">
              <Inbox className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900">Reconciliation Inbox</h1>
              <p className="text-sm text-slate-500">
                One place to clear the bank. Clear matches are linked automatically — you only review the rest.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="mr-1 flex items-center gap-2 rounded-lg border bg-white px-3 py-1.5" data-testid="recon-auto-upload-toggle-wrap">
              <span className="text-xs font-medium text-slate-600">Auto-reconcile on import</span>
              <Switch checked={autoOnUpload} onCheckedChange={toggleAutoOnUpload} data-testid="recon-auto-upload-toggle" />
              <span className="text-xs font-semibold text-slate-800">{autoOnUpload ? 'ON' : 'OFF'}</span>
            </div>
            <Button variant="outline" size="sm" onClick={() => loadDetail(selectedId)} data-testid="recon-refresh-btn">
              <RefreshCw className="mr-1.5 h-4 w-4" /> Refresh
            </Button>
            <Button
              size="sm"
              onClick={runAuto}
              disabled={!selectedId || autoRunning}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="recon-auto-btn"
            >
              <Zap className={`mr-1.5 h-4 w-4 ${autoRunning ? 'animate-pulse' : ''}`} />
              {autoRunning ? 'Reconciling…' : 'Auto-reconcile clear matches'}
            </Button>
          </div>
        </div>

        <PageInfoCallout storageKey="reconciliation-inbox" tone="sky" title="How this works">
          High-confidence matches to an existing SmartoERP record are linked automatically. No expense, payment or
          sale is ever created automatically — anything without a confident match waits for your review. Reconciled
          items are stored independently, so deleting or re-importing a statement never loses or duplicates them.
        </PageInfoCallout>

        {/* Statement selector */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <Select value={selectedId} onValueChange={selectStatement}>
            <SelectTrigger className="sm:w-[520px]" data-testid="recon-statement-select">
              <SelectValue placeholder="Select a bank statement" />
            </SelectTrigger>
            <SelectContent>
              {statements.map((s) => {
                const st = statementStatus(s);
                return (
                  <SelectItem key={s.id} value={s.id} data-testid={`recon-stmt-opt-${s.id}`}>
                    <span className="flex w-full items-center gap-2">
                      <span
                        className={`h-2 w-2 shrink-0 rounded-full ${st.dot}`}
                        data-testid={`recon-stmt-dot-${s.id}`}
                        data-status={st.key}
                        title={st.label}
                      />
                      <span className="truncate">{(s.bank_name || 'Bank')} · {s.file_name || s.id.slice(0, 8)} · {s.txn_count} txns</span>
                      <span className={`ml-auto shrink-0 pl-2 text-xs ${st.textCls}`}>{st.label}</span>
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        {/* Per-statement progress — reuses the existing summary counts from
            /recon/inbox/{id}; no new counting logic. Reconciled vs still-needs-action. */}
        {detail && detail.total > 0 && (() => {
          const done = summary.reconciled || 0;
          const total = detail.total || 0;
          const needAction = (summary.confirm || 0) + (summary.review || 0) + (summary.auto || 0);
          const pct = total ? Math.round((done / total) * 100) : 0;
          return (
            <div className="rounded-xl border bg-white p-4" data-testid="recon-progress">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-800" data-testid="recon-progress-label">
                  {done} of {total} reconciled
                </span>
                <span className="text-xs text-slate-500">
                  {needAction > 0 ? `${needAction} still need action` : 'All clear'} · {pct}%
                </span>
              </div>
              <Progress value={pct} className="h-2" data-testid="recon-progress-bar" />
            </div>
          );
        })()}

        {/* Summary chips */}
        {detail && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="recon-summary">
            {['reconciled', 'auto', 'confirm', 'review'].map((k) => {
              const M = REC_META[k];
              const Icon = M.icon;
              return (
                <Card key={k} className="border">
                  <CardContent className="flex items-center gap-3 p-4">
                    <div className={`rounded-lg border p-2 ${M.tone}`}><Icon className="h-4 w-4" /></div>
                    <div>
                      <div className="text-2xl font-bold text-slate-900" data-testid={`recon-count-${k}`}>{summary[k] || 0}</div>
                      <div className="text-xs text-slate-500">{M.label}</div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Rows */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-3 pb-2">
            <CardTitle className="text-base">
              Transactions
              {needsActionOnly && <span className="ml-2 text-xs font-normal text-amber-700">· needs action only</span>}
            </CardTitle>
            <div className="flex items-center gap-1 rounded-lg border bg-white p-0.5" data-testid="recon-row-filter">
              <button
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${!needsActionOnly ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                onClick={() => setNeedsActionFilter(false)}
                data-testid="recon-filter-all"
              >
                All
              </button>
              <button
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${needsActionOnly ? 'bg-amber-500 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                onClick={() => setNeedsActionFilter(true)}
                data-testid="recon-filter-needs-action"
              >
                Needs action
              </button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-sm text-slate-500">Classifying…</div>
            ) : !detail || !visibleRows.length ? (
              <div className="p-8 text-center text-sm text-slate-500" data-testid="recon-empty">
                {needsActionOnly && (detail?.rows || []).length
                  ? 'Nothing left to review — all clear matches are reconciled. 🎉'
                  : 'No transactions to reconcile.'}
              </div>
            ) : (
              <div className="divide-y">
                {visibleRows.map((row) => (
                  <Row
                    key={row.txn_index}
                    row={row}
                    onAction={(type) => setDialog({ type, row })}
                    onUnlink={() => unlink(row)}
                    onReviewLater={() => applyAction({ txn_index: row.txn_index, action: 'review_later' })}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Action dialogs */}
      {dialog?.type === 'link_existing' && (
        <LinkExistingDialog
          statementId={selectedId}
          row={dialog.row}
          branches={branches}
          onClose={() => setDialog(null)}
          onConfirm={(collection, id) => applyAction({
            txn_index: dialog.row.txn_index, action: 'link_existing',
            existing_collection: collection, existing_id: id,
          })}
        />
      )}
      {dialog?.type === 'add_expense' && (
        <AddExpenseDialog
          row={dialog.row}
          branches={branches}
          onClose={() => setDialog(null)}
          onConfirm={(fields) => applyAction({ txn_index: dialog.row.txn_index, action: 'add_expense', ...fields })}
        />
      )}
      {dialog?.type === 'add_payment' && (
        <AddPaymentDialog
          row={dialog.row}
          branches={branches}
          onClose={() => setDialog(null)}
          onConfirm={(fields) => applyAction({ txn_index: dialog.row.txn_index, action: 'add_payment', ...fields })}
        />
      )}
      {dialog?.type === 'pass_through' && (
        <ConfirmDialog
          title="Record as Pass-Through"
          body="Record this as a pass-through advance to the resolved employee/partner. It stays separate from income and expense."
          row={dialog.row}
          branches={branches}
          action="pass_through"
          onClose={() => setDialog(null)}
          onConfirm={() => applyAction({ txn_index: dialog.row.txn_index, action: 'pass_through' })}
        />
      )}
    </DashboardLayout>
  );
}

function Row({ row, onAction, onUnlink, onReviewLater }) {
  const M = REC_META[row.recommendation] || REC_META.review;
  const Icon = M.icon;
  const isIn = row.direction === 'in';
  const DirIcon = isIn ? ArrowDownCircle : ArrowUpCircle;
  const reconciled = row.recommendation === 'reconciled';

  return (
    <div className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between" data-testid={`recon-row-${row.txn_index}`}>
      <div className="flex min-w-0 items-start gap-3">
        <DirIcon className={`mt-0.5 h-5 w-5 shrink-0 ${isIn ? 'text-emerald-600' : 'text-rose-600'}`} />
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate font-medium text-slate-900">{row.description || '(no description)'}</span>
            <Badge variant="outline" className={`shrink-0 border text-xs ${M.tone}`} data-testid={`recon-status-${row.txn_index}`}>
              <Icon className="mr-1 h-3 w-3" />{M.label}
            </Badge>
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-500">
            <span>{row.date}</span>
            <span>·</span>
            <span className={`font-semibold ${isIn ? 'text-emerald-700' : 'text-rose-700'}`}>{cur(row.amount)}</span>
            {row.kind && row.kind !== 'unknown' && (<><span>·</span><span className="capitalize">{row.kind.replace(/_/g, ' ')}</span></>)}
          </div>
          {row.note && <div className="mt-1 max-w-2xl text-xs text-slate-400">{row.note}</div>}
        </div>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-2">
        {reconciled ? (
          <Button variant="outline" size="sm" onClick={onUnlink} data-testid={`recon-unlink-${row.txn_index}`}>
            Unlink
          </Button>
        ) : (
          <>
            <Button variant="outline" size="sm" onClick={() => onAction('link_existing')} data-testid={`recon-link-${row.txn_index}`}>
              <Link2 className="mr-1 h-3.5 w-3.5" /> Link Existing
            </Button>
            <Button variant="outline" size="sm" onClick={() => onAction('add_expense')} data-testid={`recon-expense-${row.txn_index}`}>
              <Receipt className="mr-1 h-3.5 w-3.5" /> Add Expense
            </Button>
            <Button variant="outline" size="sm" onClick={() => onAction('add_payment')} data-testid={`recon-payment-${row.txn_index}`}>
              <Banknote className="mr-1 h-3.5 w-3.5" /> Add Payment
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" data-testid={`recon-more-${row.txn_index}`}>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onAction('pass_through')} data-testid={`recon-passthrough-${row.txn_index}`}>
                  <HandCoins className="mr-2 h-4 w-4" /> Pass-Through
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onReviewLater} data-testid={`recon-reviewlater-${row.txn_index}`}>
                  <Clock className="mr-2 h-4 w-4" /> Review Later
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/banking-quick-link"><ExternalLink className="mr-2 h-4 w-4" /> Advanced (Quick-link)</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/bank-statements/reconcile"><ExternalLink className="mr-2 h-4 w-4" /> Reconciliation Cockpit</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}
      </div>
    </div>
  );
}

function LinkExistingDialog({ statementId, row, branches, onClose, onConfirm }) {
  const [candidates, setCandidates] = useState(null);
  useEffect(() => {
    api.get('/banking/quick-link/duplicate-candidates', {
      params: { row_id: `${statementId}::${row.txn_index}`, row_type: 'statement_txn', statement_id: statementId, txn_index: row.txn_index },
    }).then(({ data }) => setCandidates(data.candidates || [])).catch(() => setCandidates([]));
  }, [statementId, row.txn_index]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="recon-link-dialog">
        <DialogHeader>
          <DialogTitle>Link to an existing record</DialogTitle>
          <DialogDescription>
            Pick the existing SmartoERP record that this bank line already represents. No new record is created.
          </DialogDescription>
        </DialogHeader>
        <PostPreview row={row} branches={branches} action="link_existing" counterparty="Existing record (selected below)" />
        <div className="max-h-[40vh] space-y-2 overflow-y-auto">
          {candidates === null ? (
            <div className="p-4 text-center text-sm text-slate-500">Searching…</div>
          ) : candidates.length === 0 ? (
            <div className="p-4 text-center text-sm text-slate-500">
              No matching existing record found within the date/amount window.
            </div>
          ) : candidates.map((c) => (
            <button
              key={`${c.collection}-${c.id}`}
              className="flex w-full items-center justify-between rounded-lg border p-3 text-left hover:bg-slate-50"
              onClick={() => onConfirm(c.collection, c.id)}
              data-testid={`recon-candidate-${c.id}`}
            >
              <div>
                <div className="font-medium text-slate-900">{c.party_name || c.description || '—'}</div>
                <div className="text-xs text-slate-500">{c.collection} · {c.date}</div>
              </div>
              <div className="font-semibold text-slate-700">{cur(c.amount)}</div>
            </button>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddExpenseDialog({ row, branches, onClose, onConfirm }) {
  const [category, setCategory] = useState('');
  const [branchId, setBranchId] = useState('');
  const [description, setDescription] = useState(row.description || '');
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="recon-expense-dialog">
        <DialogHeader>
          <DialogTitle>Add as Expense</DialogTitle>
          <DialogDescription>Book this bank debit as an expense.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Category</label>
            <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. utilities, rent" data-testid="recon-expense-category" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Branch</label>
            <Select value={branchId} onValueChange={setBranchId}>
              <SelectTrigger data-testid="recon-expense-branch"><SelectValue placeholder="Default (statement branch)" /></SelectTrigger>
              <SelectContent>
                {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Description</label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} data-testid="recon-expense-description" />
          </div>
        </div>
        <PostPreview
          row={row}
          branches={branches}
          action="add_expense"
          branchOverride={branchId}
          counterparty={category.trim() ? `Expense · ${category.trim()}` : 'Expense'}
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onConfirm({ category: category || undefined, branch_id: branchId || undefined, description })} data-testid="recon-expense-submit">
            Book Expense
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddPaymentDialog({ row, branches, onClose, onConfirm }) {
  const [payeeType, setPayeeType] = useState('supplier');
  const [entities, setEntities] = useState([]);
  const [payeeId, setPayeeId] = useState('');
  const meta = PAYEE_TYPES.find((p) => p.key === payeeType);
  const payeeName = entities.find((e) => e.id === payeeId);
  const cpLabel = payeeName ? (payeeName[meta.nameField] || payeeName.name || meta.label) : `${meta.label} payment`;

  useEffect(() => {
    setPayeeId('');
    api.get(meta.endpoint).then(({ data }) => setEntities(data || [])).catch(() => setEntities([]));
  }, [payeeType]); // eslint-disable-line

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="recon-payment-dialog">
        <DialogHeader>
          <DialogTitle>Add Payment</DialogTitle>
          <DialogDescription>
            Create a bank payment to a known {payeeType}. Branch stays as the statement/owner branch (inter-branch logic preserved).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Payee type</label>
            <Select value={payeeType} onValueChange={setPayeeType}>
              <SelectTrigger data-testid="recon-payment-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAYEE_TYPES.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">{meta.label}</label>
            <Select value={payeeId} onValueChange={setPayeeId}>
              <SelectTrigger data-testid="recon-payment-entity"><SelectValue placeholder={`Select ${meta.label.toLowerCase()}`} /></SelectTrigger>
              <SelectContent>
                {entities.map((e) => <SelectItem key={e.id} value={e.id}>{e[meta.nameField] || e.name || e.id}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600">
            Amount: <span className="font-semibold">{cur(row.amount)}</span>
          </div>
        </div>
        <PostPreview row={row} branches={branches} action="add_payment" counterparty={cpLabel} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            disabled={!payeeId}
            onClick={() => onConfirm({ payee_type: payeeType, payee_id: payeeId })}
            data-testid="recon-payment-submit"
          >
            Create Payment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmDialog({ title, body, row, branches, action, onClose, onConfirm }) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="recon-confirm-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{body}</DialogDescription>
        </DialogHeader>
        {row && <PostPreview row={row} branches={branches} action={action || 'pass_through'} counterparty="Pass-through advance" />}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={onConfirm} data-testid="recon-confirm-submit">Confirm</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Read-only "Confirm & Post Preview". Shows what the EXISTING reconciliation /
// posting logic will do, using ONLY data the classifier already returned for
// this row (amount, direction, branch, fee/VAT/net where applicable) plus the
// action + counterparty picked in the dialog. It never calls a posting/preview
// API and never mutates anything — purely presentational. GL account codes are
// intentionally NOT invented; we show the double-entry direction (Bank vs the
// counterparty) which is derivable from the existing cash direction.
function PostPreview({ row, branches, action, counterparty, branchOverride }) {
  const isIn = row.direction === 'in';
  const bId = branchOverride || row.branch_id || row.paying_branch_id;
  const branchName = (branches || []).find((b) => b.id === bId)?.name;
  const debit = isIn ? 'Bank' : counterparty;
  const credit = isIn ? counterparty : 'Bank';

  const num = (v) => (typeof v === 'number' && !Number.isNaN(v));
  const fee = num(row.expected_fee) ? row.expected_fee : null;
  const vat = num(row.vat_on_fee) ? row.vat_on_fee : null;
  const net = num(row.expected_net) ? row.expected_net
    : (num(row.unexplained_difference) ? row.unexplained_difference : null);
  const netLabel = num(row.expected_net) ? 'Net after fees' : 'Unexplained difference';

  const actionText = {
    link_existing: 'Link this bank line to an existing record — no new accounting entry is posted; the existing entry stays.',
    add_expense: 'Create an expense and mark this bank line reconciled.',
    add_payment: 'Create a bank payment and mark this bank line reconciled.',
    pass_through: 'Record a pass-through advance (kept separate from income & expense).',
  }[action] || 'Reconcile this bank line.';

  const Line = ({ label, value, testid, valueCls = 'text-slate-800' }) => (
    <div className="flex items-center justify-between gap-3 py-0.5">
      <span className="text-xs text-slate-500">{label}</span>
      <span className={`text-xs font-medium ${valueCls}`} data-testid={testid}>{value}</span>
    </div>
  );

  return (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-3" data-testid="recon-post-preview">
      <div className="mb-1.5 flex items-center gap-2">
        <FileText className="h-4 w-4 text-indigo-600" />
        <span className="text-sm font-semibold text-indigo-900">Confirm &amp; post preview</span>
        <span className="ml-auto rounded-full bg-white/70 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-600">Preview only</span>
      </div>
      <p className="mb-2 text-xs text-slate-600" data-testid="recon-preview-action">{actionText}</p>
      <div className="rounded-md bg-white/70 p-2">
        {action !== 'link_existing' && (
          <>
            <Line label="Debit" value={debit} testid="recon-preview-debit" />
            <Line label="Credit" value={credit} testid="recon-preview-credit" />
          </>
        )}
        <Line label="Amount" value={cur(row.amount)} testid="recon-preview-amount" valueCls={isIn ? 'text-emerald-700' : 'text-rose-700'} />
        <Line label="Direction" value={isIn ? 'Money in (bank receipt)' : 'Money out (bank payment)'} />
        <Line label="Branch" value={branchName || (bId ? bId : 'Owner branch preserved')} testid="recon-preview-branch" />
        <Line label="Transaction / payee" value={counterparty} />
        {row.description && <Line label="Reference" value={row.description} />}
        {fee != null && <Line label="Bank fee" value={cur(fee)} />}
        {vat != null && <Line label="VAT on fee" value={cur(vat)} />}
        {net != null && <Line label={netLabel} value={cur(net)} testid="recon-preview-net" />}
      </div>
      <p className="mt-2 text-[11px] text-slate-500">
        Nothing is posted until you confirm. Values come from the current classified data; the server re-checks before posting and skips anything already reconciled.
      </p>
    </div>
  );
}
